import { useEffect, useState } from 'react'

/**
 * Whole seconds left until `deadline` (milliseconds since the epoch), never below zero.
 *
 * Returns `null` until the page has mounted, so the server render and the first browser render match.
 * Every update is recomputed from the absolute deadline and the current clock, never by counting ticks,
 * so the value is identical in every timezone and corrects itself the moment a throttled, hidden or
 * sleeping tab becomes active again.
 */
export function useCountdown(deadline: number): number | null {
    const [secondsLeft, setSecondsLeft] = useState<number | null>(null)

    useEffect(() => {
        if (!Number.isFinite(deadline)) {
            console.error('Countdown: the deadline in src/constants/campaign.ts is not a valid date.')
            return
        }

        let timer: ReturnType<typeof setTimeout> | undefined

        const tick = () => {
            clearTimeout(timer)

            const remaining = Math.max(0, deadline - Date.now())

            // Rounded up, so the display reaches zero exactly at the deadline, never a second early.
            setSecondsLeft(Math.ceil(remaining / 1000))

            // Wake just after the next whole second of remaining time instead of polling.
            if (remaining > 0) timer = setTimeout(tick, (remaining % 1000 || 1000) + 25)
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') tick()
        }

        tick()

        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('focus', tick)
        window.addEventListener('pageshow', tick)

        return () => {
            clearTimeout(timer)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('focus', tick)
            window.removeEventListener('pageshow', tick)
        }
    }, [deadline])

    return secondsLeft
}
